import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Undo2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * UndoManager
 * -----------
 * Mounted globally. Provides:
 *  - Ctrl+Z / Cmd+Z keyboard shortcut → restores the most recently deleted item.
 *  - Floating "Panic Undo" button (bottom-right) — visible whenever trash has items.
 *  - Badge showing how many recoverable items exist.
 *  - Cross-page refresh: on successful undo the page reloads so restored data appears everywhere.
 *
 * How other pages hook in:
 *  - After any DELETE, dispatch `window.dispatchEvent(new CustomEvent('app:trash-updated'))`
 *    so this component refetches the trash count immediately.
 */
const UndoManager = () => {
  const navigate = useNavigate();
  const [trashCount, setTrashCount] = useState(0);
  const [latestLabel, setLatestLabel] = useState('');
  const [isUndoing, setIsUndoing] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  const fetchTrash = useCallback(async () => {
    try {
      const token = localStorage.getItem('cse_token');
      if (!token) { setLoggedIn(false); setTrashCount(0); return; }
      setLoggedIn(true);
      const res = await axios.get(`${API}/trash`, { headers: { Authorization: `Bearer ${token}` } });
      setTrashCount(res.data.length);
      setLatestLabel(res.data[0]?.batchLabel || '');
    } catch {
      // silent — trash is optional
    }
  }, []);

  const undoLast = useCallback(async () => {
    if (isUndoing) return;
    setIsUndoing(true);
    try {
      const token = localStorage.getItem('cse_token');
      const res = await axios.post(`${API}/trash/undo-last`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Restored: ${res.data.batchLabel || 'item'}`);
      // Reload the current page so restored data shows up across all views
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Nothing to undo';
      toast.error(detail);
      setIsUndoing(false);
    }
  }, [isUndoing]);

  useEffect(() => {
    fetchTrash();
    const onKey = (e) => {
      // Ctrl+Z (Windows/Linux) or Cmd+Z (Mac). Skip if focus is inside a text field
      // so it doesn't hijack the browser's built-in text undo.
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      const isTextField = tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable;
      const isCtrl = (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey;
      if (isCtrl && (e.key === 'z' || e.key === 'Z') && !isTextField) {
        e.preventDefault();
        undoLast();
      }
    };
    const onTrashUpdate = () => fetchTrash();
    window.addEventListener('keydown', onKey);
    window.addEventListener('app:trash-updated', onTrashUpdate);
    // Poll every 30s as a safety net (in case of external changes)
    const interval = setInterval(fetchTrash, 30000);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('app:trash-updated', onTrashUpdate);
      clearInterval(interval);
    };
  }, [fetchTrash, undoLast]);

  if (!loggedIn || trashCount === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2" data-testid="undo-manager">
      {/* Panic Undo button */}
      <button
        onClick={undoLast}
        disabled={isUndoing}
        title={`Undo last delete${latestLabel ? `: ${latestLabel}` : ''} (Ctrl+Z)`}
        data-testid="panic-undo-btn"
        className="group flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-all disabled:opacity-60"
      >
        <Undo2 className="w-4 h-4" />
        <span>Undo Delete</span>
        <kbd className="hidden sm:inline-block ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-white/25 rounded">Ctrl+Z</kbd>
        <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/25 rounded-full">{trashCount}</span>
      </button>
      {/* Open Trash */}
      <button
        onClick={() => navigate('/trash')}
        title="Open Trash / Recycle Bin"
        data-testid="open-trash-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow bg-slate-800 hover:bg-slate-900 text-white text-xs"
      >
        <Trash2 className="w-3.5 h-3.5" />
        <span>View Trash</span>
      </button>
    </div>
  );
};

export default UndoManager;
